import axios from 'axios';
import { config } from '../config';
import SessionService from './SessionService';
import { normalizeTemperature, type RevenueRadar, type RevenueSettings, type Temperature } from './revenueTypes';

export * from './revenueTypes';

const API_URL = config.API_BASE_URL;

const authHeaders = () => ({
  Accept: 'application/json',
  Authorization: `Bearer ${SessionService.getToken()}`,
});

export interface Contact {
  id: string;
  name: string;
  number: string;
  avatar_url?: string;
  sector_id?: number;
  tag_id?: number;
  status: boolean;
  email?: string;
  notes?: string;
  created_at: Date;
  updated_at: Date;
  ai_active: number; // 0 = human, 1 = AI (int4)
}

export interface CardContact {
  id: number;
  name?: string | null;
  number?: string | null;
  email?: string | null;
  notes?: string | null;
  isActive?: boolean;
  priority?: string | null;
  tagId?: number | null;
  assignedTo?: number | null;
}

export interface Card {
  id: number;
  contactId: number;
  columnId: number;
  position: number;
  sectorId: number;
  createdAt: string;
  contact: CardContact | null;
  /** Assunto da oportunidade (card = oportunidade; o mesmo contato pode ter vários cards). */
  title?: string | null;
  estimatedValue?: number | null;
  currency?: string | null;
  createdBy?: string | null;
  /** Quantos cards (oportunidades) o contato tem no setor. */
  opportunityCount?: number;
  /** Temperatura da oportunidade (rótulo interno, nunca dito ao cliente). */
  temperature?: Temperature | null;
  temperatureEvidence?: string | null;
  /** ai | user | registrar */
  temperatureSource?: string | null;
  wonAt?: string | null;
  lostAt?: string | null;
  /** Dias sem fala do cliente nem mudança de etapa (só oportunidades em andamento). */
  daysStalled?: number | null;
  /** Parada há mais dias que o limite da conexão (Radar de Receita). */
  isStalled?: boolean;
}

function normalizeCard(raw: any): Card {
  const contactRaw = raw?.contact ?? raw?.Contact ?? null;
  const contactId = Number(raw?.contactId ?? raw?.ContactId ?? contactRaw?.id ?? contactRaw?.Id ?? 0);
  const contact: CardContact | null = contactRaw
    ? {
        id: Number(contactRaw.id ?? contactRaw.Id ?? contactId),
        name: contactRaw.name ?? contactRaw.Name ?? null,
        number: contactRaw.number ?? contactRaw.Number ?? null,
        email: contactRaw.email ?? contactRaw.Email ?? null,
        notes: contactRaw.notes ?? contactRaw.Notes ?? null,
        isActive: Boolean(contactRaw.isActive ?? contactRaw.IsActive ?? true),
        priority: contactRaw.priority ?? contactRaw.Priority ?? null,
        tagId: (contactRaw.tagId ?? contactRaw.TagId ?? null) as number | null,
        assignedTo: (contactRaw.assignedTo ?? contactRaw.AssignedTo ?? null) as number | null,
      }
    : null;

  return {
    id: Number(raw?.id ?? raw?.Id ?? 0),
    contactId,
    columnId: Number(raw?.columnId ?? raw?.ColumnId ?? 0),
    position: Number(raw?.position ?? raw?.Position ?? 0),
    sectorId: Number(raw?.sectorId ?? raw?.SectorId ?? 0),
    createdAt: String(raw?.createdAt ?? raw?.CreatedAt ?? ''),
    contact,
    title: raw?.title ?? raw?.Title ?? null,
    estimatedValue: raw?.estimatedValue ?? raw?.EstimatedValue ?? null,
    currency: raw?.currency ?? raw?.Currency ?? null,
    createdBy: raw?.createdBy ?? raw?.CreatedBy ?? null,
    opportunityCount: Number(raw?.opportunityCount ?? raw?.OpportunityCount ?? 1) || 1,
    temperature: normalizeTemperature(raw?.temperature ?? raw?.Temperature),
    temperatureEvidence: raw?.temperatureEvidence ?? raw?.TemperatureEvidence ?? null,
    temperatureSource: raw?.temperatureSource ?? raw?.TemperatureSource ?? null,
    wonAt: raw?.wonAt ?? raw?.WonAt ?? null,
    lostAt: raw?.lostAt ?? raw?.LostAt ?? null,
    daysStalled: (raw?.daysStalled ?? raw?.DaysStalled ?? null) as number | null,
    isStalled: Boolean(raw?.isStalled ?? raw?.IsStalled ?? false),
  };
}

/** Oportunidade (card) do contato, com a coluna e a última movimentação. */
export interface ContactOpportunity {
  id: number;
  title: string | null;
  columnId: number | null;
  columnName: string | null;
  columnKind: string | null;
  isClosed: boolean;
  isEntry: boolean;
  isPrimary: boolean;
  estimatedValue: number | null;
  currency: string | null;
  createdAt: string;
  createdBy: string | null;
  lastMove: { trigger: string; outcome: string; evidence: string | null; actorUserId: number | null; at: string } | null;
  temperature?: Temperature | null;
  temperatureEvidence?: string | null;
  temperatureConfidence?: string | null;
  temperatureReason?: string | null;
  temperatureSource?: string | null;
  temperatureUpdatedAt?: string | null;
  wonAt?: string | null;
  lostAt?: string | null;
}

/** Correção registrada: valor antigo → novo, autor e hora (CRM-06). */
export interface FieldChange {
  id: number;
  cardId: number | null;
  entity: 'opportunity' | 'contact' | string;
  field: string;
  oldValue: string | null;
  newValue: string | null;
  actorUserId: number | null;
  actorName: string | null;
  actorKind: string;
  source: string | null;
  createdAt: string;
}

/** Origem original, canal atual e canais conhecidos — informações distintas. */
export interface ContactOriginInfo {
  kind: string;
  label: string;
  campaign: string | null;
  detail: string | null;
  firstSeenAtUtc: string | null;
  source: string;
  currentChannel: string;
  knownChannels: string[];
}

export const listContactOpportunities = async (sectorId: number, contactId: number): Promise<ContactOpportunity[]> => {
  const { data } = await axios.get<ContactOpportunity[]>(`${API_URL}/cards/contact/${contactId}/opportunities`, {
    params: { sectorId },
    headers: authHeaders(),
  });
  return Array.isArray(data) ? data : [];
};

export const createOpportunity = async (payload: {
  sectorId: number;
  contactId: number;
  columnId?: number | null;
  title: string;
  estimatedValue?: number | null;
  currency?: string | null;
}): Promise<{ id: number; columnId: number; title: string }> => {
  const { data } = await axios.post(`${API_URL}/cards/opportunities`, payload, { headers: authHeaders() });
  return data;
};

export const updateOpportunity = async (
  cardId: number,
  patch: { title?: string | null; estimatedValue?: number | null; currency?: string | null; clearValue?: boolean },
): Promise<{ changed: number }> => {
  const { data } = await axios.patch(`${API_URL}/cards/${cardId}/opportunity`, patch, { headers: authHeaders() });
  return data;
};

/** Corrige a temperatura da oportunidade (null apaga); fica no histórico e a IA respeita até o cliente falar de novo. */
export const setOpportunityTemperature = async (
  cardId: number,
  temperature: Temperature | null,
): Promise<{ changed: number; previous: string | null; temperature: Temperature | null }> => {
  const { data } = await axios.patch(`${API_URL}/cards/${cardId}/temperature`, { temperature }, { headers: authHeaders() });
  return data;
};

export const getRevenueRadar = async (sectorId: number): Promise<RevenueRadar> => {
  const { data } = await axios.get<RevenueRadar>(`${API_URL}/cards/revenue`, { params: { sectorId }, headers: authHeaders() });
  return data;
};

export const saveRevenueSettings = async (sectorId: number, settings: RevenueSettings): Promise<RevenueSettings> => {
  const { data } = await axios.put(`${API_URL}/cards/revenue/settings`, { sectorId, ...settings }, { headers: authHeaders() });
  return data;
};

export const listFieldChanges = async (sectorId: number, contactId: number): Promise<FieldChange[]> => {
  const { data } = await axios.get<FieldChange[]>(`${API_URL}/cards/contact/${contactId}/history`, {
    params: { sectorId },
    headers: authHeaders(),
  });
  return Array.isArray(data) ? data : [];
};

export interface LeadFormAnswerRow {
  label: string;
  value: string;
}

/** Um envio de formulário (Google Ads, site ou integração) do contato: respostas como o lead mandou. */
export interface LeadFormSubmission {
  eventId: string;
  receivedAtUtc: string;
  integrationName: string;
  sourceType: string;
  originLabel: string;
  detail: string | null;
  campaignId: string | null;
  formId: string | null;
  answers: LeadFormAnswerRow[];
  redacted: boolean;
}

export const getContactLeadForms = async (sectorId: number, contactId: number): Promise<LeadFormSubmission[]> => {
  const { data } = await axios.get<LeadFormSubmission[]>(`${API_URL}/cards/contact/${contactId}/lead-forms`, {
    params: { sectorId },
    headers: authHeaders(),
  });
  return Array.isArray(data) ? data : [];
};

export const getContactOrigin = async (sectorId: number, contactId: number): Promise<ContactOriginInfo | null> => {
  try {
    const { data } = await axios.get<ContactOriginInfo>(`${API_URL}/cards/contact/${contactId}/origin`, {
      params: { sectorId },
      headers: authHeaders(),
    });
    return data ?? null;
  } catch {
    return null;
  }
};

/** Identificador de canal de um cliente: visto no canal (verified) ou informado (declared). */
export interface ContactIdentityInfo {
  id: number;
  channel: string;
  channelLabel: string;
  accountKey: string;
  value: string;
  status: 'verified' | 'declared' | string;
  source: string;
  handle: string | null;
  displayName: string | null;
  firstSeenAtUtc: string | null;
  lastSeenAtUtc: string | null;
}

export interface ContactIdentityEventInfo {
  id: number;
  kind: 'observed' | 'verified' | 'declared' | 'declared_conflict' | 'observed_conflict' | string;
  actor: string;
  otherContactId: number | null;
  otherContactName: string | null;
  channel: string | null;
  value: string | null;
  quote: string | null;
  createdAtUtc: string;
}

export interface ContactIdentitiesInfo {
  identities: ContactIdentityInfo[];
  events: ContactIdentityEventInfo[];
}

export const getContactIdentities = async (sectorId: number, contactId: number): Promise<ContactIdentitiesInfo | null> => {
  try {
    const { data } = await axios.get<ContactIdentitiesInfo>(`${API_URL}/cards/contact/${contactId}/identities`, {
      params: { sectorId },
      headers: authHeaders(),
    });
    return data ? { identities: data.identities ?? [], events: data.events ?? [] } : null;
  } catch {
    return null;
  }
};

export interface CreateCardRequestDTO {
  title: string;
  content?: string;
  email?: string;
  phone?: string;
  status?: string;
  assignedTo?: string;
  assigneeAvatar?: string;
  lastContact?: Date;
  priority?: 'high' | 'medium' | 'low';
  contactId: number;
  tagId?: number;
  columnId: number;
  sectorId: number;
  position: number;
}

export interface UpdateCardRequestDTO {
  id: string;
  contactId: number;
  columnId: number;
  position: number;
  sectorId: number;
  createdAt?: string;
}

export const getContacts = async (sectorId: number): Promise<Contact[]> => {
  try {
    const response = await axios.get(`${API_URL}/contatos`, {
      params: { sectorId },
      headers: {
        'Accept': '*/*',
      },
    });
    return response.data.map((contact: any) => ({
      ...contact,
      id: contact.id.toString()
    }));
  } catch (error) {
    throw new Error('Failed to get contacts: ' + error);
  }
};

export const createCard = async (cardData: CreateCardRequestDTO): Promise<Card> => {
  try {
    if (!cardData.contactId) {
      throw new Error('A contact must be selected');
    }

    const position = Math.max(1, Math.min(99, cardData.position || 1));
    const response = await axios.post(`${API_URL}/cards`, {
      ...cardData,
      position
    }, { headers: authHeaders() });
    return {
      ...response.data,
      id: response.data.id.toString(),
      contato: response.data.contato ? {
        ...response.data.contato,
        id: response.data.contato.id.toString()
      } : undefined
    };
  } catch (error) {
    throw new Error(`Failed to create card: ${error}`);
  }
};

export const updateCard = async (id: string, data: UpdateCardRequestDTO): Promise<Card> => {
  try {
    const response = await axios.put<Card>(`${API_URL}/Card/${id}`, data, { headers: authHeaders() });
    return response.data;
  } catch (error) {
    throw new Error(`Failed to update card with id ${id}: ${error}`);
  }
};

export const getCard = async (id: string): Promise<Card> => {
  try {
    const response = await axios.get(`${API_URL}/cards/${id}`, {
      headers: authHeaders(),
    });
    return {
      ...response.data,
      id: response.data.id.toString(),
      contato: response.data.contato ? {
        ...response.data.contato,
        id: response.data.contato.id.toString()
      } : undefined
    };
  } catch (error) {
    throw new Error(`Failed to get card with id ${id}: ${error}`);
  }
};

/** perColumn > 0 = só as primeiras N cards de cada coluna (board paginado); omitido = todas. */
export const getCards = async (sectorId: number, perColumn?: number): Promise<Card[]> => {
  try {
    const params = new URLSearchParams({ sectorId: String(sectorId) });
    if (perColumn && perColumn > 0) params.set('perColumn', String(perColumn));
    const response = await axios.get(
      `${API_URL}/Cards?${params}`,
      { headers: authHeaders() }
    );

    const raw = Array.isArray(response.data) ? response.data : [];

    return raw.map(normalizeCard).filter((c) => c.id > 0 && c.columnId > 0);
  } catch (error: any) {
    console.error('Erro detalhado ao obter cards:', error);
    if (error.response) {
      console.error('Resposta do servidor:', error.response.data);
      console.error('Status:', error.response.status);
    }
    throw new Error('Falha ao obter cards: ' + error);
  }
};

export interface CardColumnStats {
  /** columnId → total de cards da coluna. */
  counts: Record<string, number>;
  /** columnId → soma em R$ dos cards com valor em reais (todos, não só os do board). */
  valueBrl: Record<string, number>;
}

/** Total real de cards e soma em R$ por coluna, para o board paginado. */
export const getCardColumnStats = async (sectorId: number): Promise<CardColumnStats> => {
  const response = await axios.get(`${API_URL}/Cards/counts?sectorId=${sectorId}`, { headers: authHeaders() });
  const counts: Record<string, number> = {};
  const valueBrl: Record<string, number> = {};
  for (const row of Array.isArray(response.data) ? response.data : []) {
    const id = row?.columnId ?? row?.ColumnId;
    if (id == null) continue;
    counts[String(id)] = Number(row?.count ?? row?.Count ?? 0);
    valueBrl[String(id)] = Number(row?.valueBrl ?? row?.ValueBrl ?? 0) || 0;
  }
  return { counts, valueBrl };
};

/** Total real de cards por coluna (columnId → total), para o board paginado. */
export const getCardColumnCounts = async (sectorId: number): Promise<Record<string, number>> =>
  (await getCardColumnStats(sectorId)).counts;

export const deleteCard = async (id: string): Promise<void> => {
  try {
    await axios.delete(`${API_URL}/cards/${id}`, { headers: authHeaders() });
  } catch (error) {
    throw new Error(`Failed to delete card with id ${id}: ${error}`);
  }
};

export const moveCard = async (id: string, newColumnId: number, newPosition: number): Promise<void> => {
  try {
    await axios.put(`${API_URL}/cards/${id}/move`, { newColumnId, newPosition }, {
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders(),
      },
    });
  } catch (error) {
    throw new Error(`Failed to move card with id ${id} to column with id ${newColumnId}: ${error}`);
  }
};
